
import Vue from 'vue';
import { Component } from 'nuxt-property-decorator';
import { MetaInfo } from 'vue-meta';
import { MyHotelLocalNavi } from '../-components';
import HotelViewRoot from '../../-index';
import { RestaurantCommons, Restaurant, HotelCuisineContents } from '~/schemes';
import html2text from '~/helpers/html2text';
import {
  HCuisineContentSetList,
  HThemeDecoration,
  HSimpleLinks,
  HIconLink,
} from '~/components';

@Component<MyView>({
  inject: ['hotelViewRoot'],
  async asyncData({ $commons, $api, $hotel, params, error, redirect, $error }) {
    try {
      const { hotel_slug, restaurant_id } = params;

      const hotelSource = await $hotel.getBySlug(hotel_slug);
      if (!hotelSource) {
        return error({
          statusCode: 404,
        });
      }

      /**
       * 食事処基本設定がなければ施設TOPにリダイレクトしてあげる
       */
      if (!hotelSource.restaurantSettings) {
        const location = $hotel.location(undefined, hotelSource);
        return redirect(location);
      }

      // 食事処を取得
      // * とはいっても施設詳細情報にベースはあるのでそれをまず取得
      const restaurant = hotelSource.restaurants.find(
        (restaurant) => restaurant.id === restaurant_id,
      );

      // 食事処がない場合は404
      if (!restaurant) {
        return error({
          statusCode: 404,
        });
      }

      // 食事処があるけど詳細ページがない場合は食事TOP
      if (!restaurant.hasPage) {
        const location = $hotel.location('/dining/', hotelSource);
        return redirect(location);
      }

      const [restaurantCommons, cuisineLayout] = await Promise.all([
        $commons.getRestaurant(),
        $hotel.cuisinesBySlug(hotel_slug, restaurant._id).catch((err) => {
          if (
            $api.isAxiosError(err) &&
            err.response &&
            err.response.status === 404
          ) {
            return;
          }
          throw err;
        }),
      ]);
      if (!cuisineLayout) {
        error({ statusCode: 404 });
        return;
      }

      return {
        restaurantCommons,
        restaurant,
        cuisines: cuisineLayout.cuisines,
      };
    } catch (_err) {
      $error.throw(_err);
    }
  },
  head() {
    const { hotel, restaurant, diningSectionLabel } = this;
    const { mainVisual, name: restaurantName } = restaurant;
    const description = html2text(restaurant.description).replace(/\n/g, '');
    const meta: MetaInfo['meta'] = [
      {
        hid: 'og:title',
        property: 'og:title',
        content: restaurantName,
      },
      {
        hid: 'og:url',
        property: 'og:url',
        content: `${this.$navigation.origin}/${this.$language.current}/hotels/${hotel.slug}/dining/${this.slug}/`,
      },
    ];
    const ogp = mainVisual.ogp && mainVisual.ogp.url;
    if (ogp) {
      meta.push({
        hid: 'og:image',
        property: 'og:image',
        content: ogp,
      });
    }

    if (description) {
      meta.push(
        {
          hid: 'description',
          name: 'description',
          content: description,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: description,
        },
      );
    }
    const head: MetaInfo = {
      title: `${restaurantName} | ${diningSectionLabel}`,
      meta,
    };
    return head;
  },
  render() {
    const { restaurant, hasMainVisual } = this;
    const { name, nameDecoration, ruby, catchCopy, description } = restaurant;

    return (
      <div staticClass="my-hotel-dining-detail-view">
        <MyHotelLocalNavi />
        <div
          staticClass="my-head"
          class={{
            'my-head--without-image': !hasMainVisual,
          }}
          style={this.mainVisualStyles}>
          <div staticClass="my-head__inner">
            <div staticClass="my-head__title">
              {!!nameDecoration && (
                <span staticClass="my-head__title__deco">{nameDecoration}</span>
              )}
              <h1 staticClass="my-head__title__name">{name}</h1>
              {!!ruby && <span staticClass="my-head__title__ruby">{ruby}</span>}
            </div>
          </div>
          <HThemeDecoration staticClass="my-head__deco" size="lg" />
        </div>
        {(!!catchCopy || !!description) && (
          <div class="my-lead">
            {!!catchCopy && (
              <div class="my-catch-copy">
                <div class="my-catch-copy__text" v-wysiwyg={catchCopy} />
                <HThemeDecoration staticClass="my-catch-copy__deco" size="md" />
              </div>
            )}
            {!!description && (
              <div
                class="my-description h-theme-lead"
                v-wysiwyg={description}
              />
            )}
          </div>
        )}
        <div staticClass="my-inner">
          <HCuisineContentSetList
            staticClass="my-cuisine-contents"
            commons={this.restaurantCommons}
            contents={this.cuisines}
            restaurant={this.restaurant}
          />
        </div>

        <HSimpleLinks
          staticClass="my-allergy-links"
          items={[
            {
              key: 'allergy',
              label: this.$t('guide.allergy') as string,
              href: this.$t('url.allergy') as string,
            },
          ]}
        />

        <p staticClass="my-to-index">
          <HIconLink back to={this.listLocation}>
            {this.$t('action.backToList', {
              listName: this.diningSectionLabel,
            })}
          </HIconLink>
        </p>
      </div>
    );
  },
})
export default class MyView extends Vue {
  readonly hotelViewRoot!: HotelViewRoot;

  /** 食事共通設定 */
  restaurantCommons: RestaurantCommons = null as any;

  /** 食事処 */
  restaurant: Restaurant = null as any;

  /** 料理コンテンツセット */
  cuisines: HotelCuisineContents = null as any;

  /** URLスラッグ */
  get slug() {
    return this.$route.params.restaurant_id;
  }

  /** 施設詳細情報 */
  get hotel() {
    return this.hotelViewRoot.hotel;
  }

  /** 一覧画面のルート */
  get listLocation() {
    return this.$hotel.location('/dining/', this.hotel);
  }

  /**
   * テーマで設定されている食事ナビゲーション名
   */
  get diningSectionLabel() {
    return this.$theme.getSectionLongLabelWithoutHTML('dining');
  }

  /**
   * メインビジュアルがあるか
   */
  get hasMainVisual() {
    const { wide, narrow } = this.restaurant.mainVisual;
    return !!wide || !!narrow;
  }

  /** メインビジュアル用スタイル */
  get mainVisualStyles() {
    const { wide, narrow } = this.restaurant.mainVisual;
    return {
      '--narrow-image': narrow && `url(${narrow.url})`,
      '--wide-image': wide && `url(${wide.url})`,
    };
  }
}
